import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Loader2, ArrowLeft, HelpCircle, FileText, Image as ImageIcon, Send, PlayCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, formatDate } from "@/lib/qa";
import { useNavigate } from "react-router-dom";

function Attachments({ items }) {
    if (!items || !items.length) return <p className="text-sm text-muted-foreground italic">Nessun allegato</p>;
    return (
        <ul className="space-y-2">
            {items.map((a, i) => (
                <li key={i}>
                    <a href={a.data_url} download={a.filename} className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm hover:border-primary/40">
                        {a.mime_type === "application/pdf" ? <FileText className="size-4 text-rose-500" /> : <ImageIcon className="size-4 text-sky-500" />}
                        <span className="truncate">{a.filename}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{(a.size_bytes / 1024).toFixed(0)} KB</span>
                    </a>
                </li>
            ))}
        </ul>
    );
}

export default function AdminRequestDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [q, setQ] = useState(null);
    const [error, setError] = useState("");
    const [body, setBody] = useState("");
    const [profName, setProfName] = useState("");
    const [profRole, setProfRole] = useState("");
    const [saving, setSaving] = useState(false);
    const [taking, setTaking] = useState(false);

    const load = () => API.get(`/questions/${id}`).then(({ data }) => {
        setQ(data);
        if (data.answer) {
            setBody(data.answer.body);
            setProfName(data.answer.professional_name);
            setProfRole(data.answer.professional_role || "");
        }
    }).catch(e => setError(apiError(e)));

    useEffect(() => { load(); }, [id]);

    const take = async () => {
        setTaking(true);
        try { await API.post(`/admin/questions/${id}/take`); toast.success("Presa in carico"); await load(); }
        catch (e) { toast.error(apiError(e)); } finally { setTaking(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            await API.post(`/admin/questions/${id}/answer`, { body, professional_name: profName, professional_role: profRole || null });
            toast.success("Risposta inviata all'utente");
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const remove = async () => {
        try { await API.delete(`/admin/questions/${id}`); toast.success("Richiesta eliminata"); navigate("/admin/richieste"); }
        catch (e) { toast.error(apiError(e)); }
    };

    if (error) return <div className="max-w-2xl mx-auto p-10"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>;
    if (!q) return <div className="p-16 text-center"><Loader2 className="size-6 mx-auto animate-spin" /></div>;

    const canSave = body.length >= 10 && profName.length >= 2 && !saving;

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin/richieste"><ArrowLeft className="size-4 mr-1" /> Gestione richieste</Link>
            </Button>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-xs font-semibold text-primary uppercase tracking-widest">Domanda</span>
                <StatusBadge status={q.status} />
            </div>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <h1 className="font-serif text-3xl font-semibold" data-testid="admin-req-detail-title">{q.title}</h1>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid="admin-req-delete"><Trash2 className="size-4 mr-1" /> Elimina</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare questa richiesta?</AlertDialogTitle>
                            <AlertDialogDescription>La domanda dell'utente e la risposta verranno rimosse in modo definitivo.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground">Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Ricevuta il {formatDate(q.created_at)}</p>

            <div className="grid lg:grid-cols-5 gap-6 mt-6">
                <div className="lg:col-span-2 space-y-4">
                    <Card className="border-l-4 border-l-primary border-slate-200">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-3"><HelpCircle className="size-4" /> Domanda</div>
                            <p className="whitespace-pre-wrap text-slate-800 leading-relaxed text-sm">{q.body}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200">
                        <CardContent className="pt-6">
                            <div className="font-semibold text-sm mb-3">Utente</div>
                            <div className="text-sm">{q.user_name}</div>
                            <div className="text-xs text-muted-foreground">{q.user_email}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200">
                        <CardContent className="pt-6">
                            <div className="font-semibold text-sm mb-3">Allegati</div>
                            <Attachments items={q.attachments} />
                        </CardContent>
                    </Card>
                </div>

                <div className="lg:col-span-3">
                    <Card className="border-slate-200">
                        <CardContent className="pt-6 space-y-4">
                            {q.status === "received" && (
                                <div className="flex items-center justify-between gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4">
                                    <div className="text-sm text-amber-900">Presa in carico ancora non effettuata.</div>
                                    <Button size="sm" onClick={take} disabled={taking} data-testid="admin-req-take">
                                        {taking ? <Loader2 className="size-4 mr-2 animate-spin" /> : <PlayCircle className="size-4 mr-2" />}
                                        Prendi in carico
                                    </Button>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label>Corpo della risposta <span className="text-xs text-muted-foreground">({body.length}/10000)</span></Label>
                                <Textarea value={body} onChange={e => setBody(e.target.value)} rows={10} maxLength={10000} data-testid="admin-answer-body" placeholder="Scrivi qui la risposta chiara e completa…" />
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Firma professionista *</Label>
                                    <Input value={profName} onChange={e => setProfName(e.target.value)} maxLength={120} data-testid="admin-answer-name" placeholder="Es. Avv. Mario Rossi" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Ruolo (opzionale)</Label>
                                    <Input value={profRole} onChange={e => setProfRole(e.target.value)} maxLength={80} data-testid="admin-answer-role" placeholder="Es. Avvocato" />
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                                La risposta è <strong>privata</strong>: la vedrà solo l'utente autore della richiesta.
                            </p>
                            <div className="flex justify-end gap-2 pt-3 border-t">
                                <Button onClick={save} disabled={!canSave} data-testid="admin-answer-save">
                                    {saving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Send className="size-4 mr-2" />}
                                    {q.answer ? "Aggiorna risposta" : "Invia risposta"}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
